<template>
  <fullscreen-dialog
    v-model="model"
    :title="$t('warehouse.offers_invoices')"
    :subtitle="editedItem && editedItem.id ? 'ID: ' + editedItem.id : undefined"
    icon="mdi-file-document-edit"
    color="primary"
    :save-text="$t('warehouse.send_offer')"
    :disabled="!valid || !editedItem.groceries.length"
    :loading="loading"
    @save="submitOffer"
  >
    <template v-slot:app-bar-actions>
      <excel-button class="mr-2" @click="exportExcel" />
      <warehouse-button-print-menu @print="print" />
    </template>

    <v-form ref="form" v-model="valid">
      <v-row>
        <v-col cols="12" lg="3" xl="2">
          <text-field
            :label="$t('warehouse.document_number')"
            v-model="editedItem.number"
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <date-picker v-model="editedItem.document_date" single-date color="white" />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <datetime-picker
            v-model="editedItem.delivery_deadline"
            single-date
            :label="$t('warehouse.delivery_deadline')"
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="3" xl="4">
          <text-area
            :label="$t('note')"
            v-model="editedItem.notes"
            rows="1"
            color="white"
          />
        </v-col>
      </v-row>
    </v-form>

    <warehouse-document-groceries-table
      :groceries="editedItem.groceries"
      :warehouse="selectedWarehouse || {}"
      :headers="headers"
      :is-supplier="true"
      @removeGrocery="removeGrocery"
    />
  </fullscreen-dialog>
</template>

<script>
import hasWarehouseDocumentDialog from "@/mixins/warehouse/hasWarehouseDocumentDialog";
import hasPrintingV2 from "@/mixins/hasPrintingV2";
import hasWarehousePrinting from "@/mixins/warehouse/hasWarehousePrinting";
import hasExcel from "@/mixins/hasExcel";
import { orderDocumentGrocery } from "@/models/warehouse";
import warehouseService from "@/services/warehouse";

export default {
  name: "SupplierPortalOffersInvoicesDialog",
  mixins: [hasWarehouseDocumentDialog, hasPrintingV2, hasWarehousePrinting, hasExcel],
  components: {},
  props: {
    editedItem: {
      type: Object,
      default: () => ({ ...orderDocumentGrocery }),
    },
  },
  methods: {
    submitOffer() {
      this.showInfoSnackbar(
        "Slanje ponude je još u razvoju. Funkcionalnost će biti dostupna uskoro."
      );
    },
    print(withStock = false) {
      const { table, line, spacer, heading, text } = this.printElements;

      const docDate = this.localeDate(this.editedItem.document_date);
      const docNumber = this.editedItem.number || "";
      const docLabel = this.$t("warehouse.offers_invoices");

      this.printDoc(
        [
          heading(docLabel + (docNumber ? " " + docNumber : "")),
          `<div style="text-align:center;font-size:11px;color:#777;margin-top:2px">${docDate}</div>`,
          spacer(12),
          table(this.getPrintHeaders(withStock), this.getPrintItems()),
          spacer(24),
          text(
            this.$t("warehouse.delivery_deadline") +
              ": " +
              (this.editedItem.delivery_deadline
                ? this.localeDate(this.editedItem.delivery_deadline)
                : ""),
            { size: 13, block: true }
          ),
          spacer(40),
          this.printElements.row("end", [
            this.printElements.col("center", [
              text(this.$t("supplier")),
              spacer(16),
              line(),
            ]),
          ]),
        ],
        { pageTitle: docLabel + " " + docNumber }
      );
    },
    async exportExcel() {
      const docNumber = this.editedItem.number || "";
      const title = `${this.$t("warehouse.offers_invoices")} ${docNumber}`.trim();
      await this.downloadExcel({
        fileName: `${title}.xlsx`,
        sheets: [
          {
            name: title.substring(0, 31),
            title,
            headers: this.getPrintHeaders(false, false),
            items: this.getPrintItems(false),
          },
        ],
      });
    },
    async save() {
      const params = {
        ...this.editedItem,
        groceries: this.editedItem.groceries.map((grocery) => ({
          grocery_id: grocery.id,
          name: grocery.name,
          code: grocery.code,
          supplier_code: grocery.supplier_code,
          measure: grocery.measure,
          amount: grocery.amount,
          gross_quantity: Number(grocery.gross_quantity) || 0,
          unit_price: Number(grocery.unit_price) || 0,
          transaction_type: this.editedItem.document_type,
        })),
      };

      try {
        this.loading = true;
        const request = warehouseService.addDocumentTransaction(params);
        await this.handleRequest(request, {
          successMessage: this.$t("purchase_order_created_successfully"),
          errorMessagePrefix: this.$t("error"),
        });
        this.model = false;
        this.$emit("success");
      } catch (error) {
        console.error("Save operation failed:", error);
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    filters() {
      return this.$store.state.warehouse.filters;
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", width: "100px" },
        { text: this.$t("code"), align: "start", value: "code", width: "150px" },
        { text: this.$t("supplier_code"), value: "supplier_code", width: "150px" },
        { text: this.$t("title"), value: "name" },
        {
          text: this.$t("quantity") + ` (${this.$t("warehouse.quantity_gross")})`,
          value: "gross_quantity",
          align: "right",
          width: "160px",
        },
        {
          text: this.$t("quantity") + ` (${this.$t("warehouse.quantity_net")})`,
          value: "amount",
          align: "right",
          width: "160px",
        },
        {
          text: this.$t("warehouse.warehouse_status"),
          value: "warehouse_stock",
          print: false,
          align: "right",
          width: "200px",
        },
        {
          text: this.$t("unit_of_measurment"),
          printText: this.$t("unit_of_measurment").replace(" ", "<br>"),
          value: "measure",
          width: "200px",
        },
        {
          text: this.$t("price"),
          value: "unit_price",
          align: "right",
          width: "140px",
        },
        {
          text: this.$t("amount"),
          value: "total_amount",
          align: "right",
          width: "140px",
          sortable: false,
        },
        { text: "", value: "actions", width: "100px" },
      ];
    },
  },
  watch: {
    model(newVal) {
      if (newVal && !this.editedItem.id) {
        const { from, to } = this.filters.dates || {};
        let note = `Po osnovu trebovanja za period od ${from} do ${to}`;
        if (from === to) note = `Po osnovu trebovanja za ${from}`;
        this.editedItem.notes = this.translate(note);
      }
    },
  },
};
</script>
